import { useCallback, useEffect, useRef, useState } from "react";

interface UseRetractableBarOptions {
  shouldRetract?: boolean;
  edge?: "top" | "bottom";
  inactivityDelayMs?: number;
  mouseEdgeThresholdPx?: number;
}

export const useRetractableBar = ({
  shouldRetract = false,
  edge = "top",
  inactivityDelayMs = 3000,
  mouseEdgeThresholdPx = 10,
}: UseRetractableBarOptions = {}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [hasMounted, setHasMounted] = useState(false);

  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollYRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setHasMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const hideBar = useCallback(() => {
    setIsVisible(false);
  }, []);

  const resetHideTimer = useCallback(() => {
    clearHideTimer();

    hideTimerRef.current = setTimeout(() => {
      hideBar();
    }, inactivityDelayMs);
  }, [clearHideTimer, hideBar, inactivityDelayMs]);

  const showBar = useCallback(() => {
    setIsVisible(true);
    resetHideTimer();
  }, [resetHideTimer]);

  useEffect(() => {
    if (shouldRetract) {
      resetHideTimer();
    } else {
      setIsVisible(true);
      clearHideTimer();
    }

    const handleScroll = () => {
      if (!shouldRetract) return;

      const currentScrollY = window.scrollY;
      const scrollDiff = currentScrollY - lastScrollYRef.current;

      if (scrollDiff < -10) {
        showBar();
      } else if (scrollDiff > 10) {
        hideBar();
        clearHideTimer();
      }

      lastScrollYRef.current = currentScrollY;
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!shouldRetract || isVisible) return;

      const nearEdge =
        edge === "top"
          ? e.clientY < mouseEdgeThresholdPx
          : e.clientY > window.innerHeight - mouseEdgeThresholdPx;

      if (nearEdge) {
        showBar();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("mousemove", handleMouseMove, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleMouseMove);
      clearHideTimer();
    };
  }, [clearHideTimer, hideBar, isVisible, mouseEdgeThresholdPx, resetHideTimer, shouldRetract, showBar, edge]);

  return {
    isVisible,
    hasMounted,
    showBar,
    hideBar,
    resetHideTimer,
  };
};
